import { notifySuccess } from '@/core/helpers/notification.helper';
import i18n from '../../i18n';
import type { AiMatchingRunStatus, AiPositionCandidate } from './ai-matching.interface';
import { getAiMatchingRun, getAiPositionCandidates, startAiMatching } from './ai-matching.service';

const AI_MATCHING_POLL_INTERVAL_MS = 5000;

export interface AiMatchingState {
   candidatesByAiPositionId: Record<number, Array<AiPositionCandidate>>;
   loadingCandidatesByAiPositionId: Record<number, boolean>;
   runningAiPositionId: number | null;
}

const AiMatchingStore = {
   namespaced: true,

   state: {
      candidatesByAiPositionId: {},
      loadingCandidatesByAiPositionId: {},
      runningAiPositionId: null,
   },

   mutations: {
      SET_CANDIDATES(state: AiMatchingState, payload: { aiPositionId: number; candidates: Array<AiPositionCandidate> }) {
         state.candidatesByAiPositionId = {
            ...state.candidatesByAiPositionId,
            [payload.aiPositionId]: payload.candidates,
         };
      },
      SET_LOADING_CANDIDATES(state: AiMatchingState, payload: { aiPositionId: number; loading: boolean }) {
         state.loadingCandidatesByAiPositionId = {
            ...state.loadingCandidatesByAiPositionId,
            [payload.aiPositionId]: payload.loading,
         };
      },
      SET_RUNNING_AI_POSITION_ID(state: AiMatchingState, aiPositionId: number | null) {
         state.runningAiPositionId = aiPositionId;
      },
   },

   actions: {
      async fetchAiPositionCandidates(context: any, payload: { aiPositionId: number; positiveVote?: boolean | null }) {
         const { aiPositionId, positiveVote = null } = payload;
         const tenantSyncId = context.rootState?.userInfo?.userInfo?.tenant?.sync_id;

         context.commit('SET_LOADING_CANDIDATES', { aiPositionId, loading: true });

         try {
            const candidates = await getAiPositionCandidates(aiPositionId, tenantSyncId, positiveVote);

            context.commit('SET_CANDIDATES', { aiPositionId, candidates });
            return candidates;
         } catch {
            context.commit('SET_CANDIDATES', { aiPositionId, candidates: [] });
            return [];
         } finally {
            context.commit('SET_LOADING_CANDIDATES', { aiPositionId, loading: false });
         }
      },

      async startAiMatchingJob(context: any, payload: { aiPositionId: number; positionId: string | number }) {
         if (context.state.runningAiPositionId !== null) {
            return [];
         }

         context.commit('SET_RUNNING_AI_POSITION_ID', payload.aiPositionId);

         try {
            const run = await startAiMatching(String(payload.positionId));
            let status = run.status;
            let completedRun: AiMatchingRunStatus | null = null;

            while (status === 'pending' || status === 'running') {
               await new Promise((resolve) => setTimeout(resolve, AI_MATCHING_POLL_INTERVAL_MS));
               completedRun = await getAiMatchingRun(String(payload.positionId), run.run_id);
               ({ status } = completedRun);
            }

            if (status === 'failed') {
               throw new Error('AI matching failed');
            }

            const candidates = await context.dispatch('fetchAiPositionCandidates', {
               aiPositionId: payload.aiPositionId,
               positiveVote: null,
            });

            notifySuccess({ title: i18n.global.t('ai-matching.matching-done') });

            return {
               candidates,
               counts: completedRun
                  ? {
                       count: completedRun.count,
                       liked_count: completedRun.liked_count,
                       disliked_count: completedRun.disliked_count,
                    }
                  : undefined,
            };
         } finally {
            context.commit('SET_RUNNING_AI_POSITION_ID', null);
         }
      },
   },
};

export default AiMatchingStore;
